import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { SeoHead } from './SeoHead'

import { usePropertyProvider } from '../../../hooks'
import { ensureAbsoluteUrl, getPropertyOgImage } from '../../../utils'

export interface PropertySeoHeadProps {
  /** ページ固有のタイトル（例: meta.rooms.title）。施設名が取れる場合は前置される。 */
  title: string
  /** ページ固有の説明文。施設に関わらずそのまま使う。 */
  description: string
}

/**
 * 施設（property）配下の公開ページ共通の SEO / OGP ヘッド。
 *
 * 静的な {@link Head} は og:image を出さないため、`/:property/rooms` などでは
 * index.html のデフォルト画像（F VILLAGE）にフォールバックしてしまう。
 * このコンポーネントは `PropertyProvider` 配下で施設データから OGP 画像を動的に
 * セットしつつ、`<title>` / `og:title` に施設名を前置して「どの施設のページか」を
 * 共有カードからも分かるようにする。
 *
 * 施設データが取得できない場合は、施設名プレフィックスも og:image も付けず、
 * 渡された `title` / `description` をそのまま出す（= 従来の静的 Head 相当の挙動）。
 */
export const PropertySeoHead: React.FC<
  PropsWithChildren<PropertySeoHeadProps>
> = ({ title, description, children }) => {
  const location = useLocation()
  const { i18n } = useTranslation()
  const propertyData = usePropertyProvider()

  const baseUrl = 'https://booking.hkdballpark.com'
  const currentUrl = `${baseUrl}${location.pathname}`

  // PropertyDetailHead と同じ施設名解決ロジック。
  // 表示言語の publication 名を優先し、無ければ先頭 publication / building 名。
  const currentLocale = i18n.language
  const publication =
    propertyData?.publications?.find((pub) => pub.locale === currentLocale) ||
    propertyData?.publications?.[0]
  const propertyName = publication?.name || propertyData?.name || ''

  // 施設名が取れたときだけ「{施設名}｜{ページ固有タイトル}」に前置する。
  const seoTitle = propertyName ? `${propertyName}｜${title}` : title

  // 施設のメイン画像を OGP 画像に。施設に画像が無ければ getPropertyOgImage が
  // デフォルト画像（F VILLAGE）を返す。index.html の静的 og:image は data-rh で
  // helmet 管理下にあり、og:image を出さないと「削除」されてしまうため、常に
  // 1つは出す（画像があれば施設画像、無ければデフォルト）。
  const propertyImages =
    propertyData?.images?.map((img) => img.url || '').filter(Boolean) || []
  const ogImage = ensureAbsoluteUrl(
    getPropertyOgImage({
      propertyId: propertyData?.id?.toString() || '',
      propertyName,
      images: propertyImages,
    }),
    baseUrl
  )

  return (
    <SeoHead
      title={seoTitle}
      description={description}
      canonical={currentUrl}
      ogTitle={seoTitle}
      ogImage={ogImage}
      ogImageAlt={propertyName || undefined}
      ogImageWidth="1200"
      ogImageHeight="630"
      ogUrl={currentUrl}
      twitterCard="summary_large_image"
      twitterSite="@ballpark_htb"
    >
      {children}
    </SeoHead>
  )
}
